import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useZones, useLocations } from '../hooks/useWmsLocations';
import {
  useSerializedItems,
  useCreateSerializedItem,
  useTransferSerializedItem,
  useChangeDeviceStatus,
} from '../hooks/useWmsInventory';
import { useDebounce } from '../hooks/useDebounce';
import { SerializedItemsFilters } from '../components/serials/SerializedItemsFilters';
import { SerializedItemsTable } from '../components/serials/SerializedItemsTable';
import { SerializedItemFormModal } from '../components/serials/SerializedItemFormModal';
import { SerializedItemTransferModal } from '../components/serials/SerializedItemTransferModal';
import { DeviceStatusChangeModal } from '../components/serials/DeviceStatusChangeModal';
import type {
  SerializedItem,
  SerializedItemStatus,
  DeviceStatus,
  CreateSerializedItemInput,
  ChangeDeviceStatusInput,
  ZonesResponse,
  LocationsResponse,
  SerializedItemsResponse,
} from '../types';

export default function SerializedItemsPage() {
  // Filter state
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [zoneId, setZoneId] = useState('all');
  const [itemStatus, setItemStatus] = useState('all');
  const [deviceStatus, setDeviceStatus] = useState('all');
  const [pageNumber, setPageNumber] = useState(0);
  const pageLength = 50;

  // Reset to first page when debounced search changes
  useEffect(() => {
    setPageNumber(0);
  }, [debouncedSearch]);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [transferItem, setTransferItem] = useState<SerializedItem | null>(null);
  const [statusChangeItem, setStatusChangeItem] = useState<SerializedItem | null>(null);

  // Queries
  const zonesQuery = useZones();
  const locationsQuery = useLocations({ page_length: 100 });
  const serializedItemsQuery = useSerializedItems({
    search: debouncedSearch || undefined,
    zone_id: zoneId === 'all' ? undefined : zoneId,
    item_status: itemStatus === 'all' ? undefined : (itemStatus as SerializedItemStatus),
    device_status: deviceStatus === 'all' ? undefined : (deviceStatus as DeviceStatus),
    page_number: pageNumber,
    page_length: pageLength,
  });

  // Mutations
  const createMutation = useCreateSerializedItem();
  const transferMutation = useTransferSerializedItem();
  const changeStatusMutation = useChangeDeviceStatus();

  const zonesData = zonesQuery.data as ZonesResponse | undefined;
  const locationsData = locationsQuery.data as LocationsResponse | undefined;
  const itemsData = serializedItemsQuery.data as SerializedItemsResponse | undefined;
  const isLoading = serializedItemsQuery.isLoading;
  const error = serializedItemsQuery.error;

  const zones = zonesData?.zones || [];
  const locations = locationsData?.locations || [];
  const items = itemsData?.items || [];
  const total = itemsData?.total || 0;

  const handleCreate = async (data: CreateSerializedItemInput) => {
    await createMutation.mutateAsync(data);
  };

  const handleTransfer = async (locationId: string) => {
    if (!transferItem) return;
    await transferMutation.mutateAsync({ id: transferItem.id, toLocationId: locationId });
  };

  const handleChangeStatus = async (data: ChangeDeviceStatusInput) => {
    if (!statusChangeItem) return;
    await changeStatusMutation.mutateAsync({ id: statusChangeItem.id, data });
  };

  const totalPages = Math.ceil(total / pageLength);

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Serialized Items</h1>
          <p className="text-muted-foreground">
            Track individual devices by serial number
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>Add Item</Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <SerializedItemsFilters
            search={search}
            onSearchChange={setSearch}
            zoneId={zoneId}
            onZoneChange={(v) => {
              setZoneId(v);
              setPageNumber(0);
            }}
            itemStatus={itemStatus}
            onItemStatusChange={(v) => {
              setItemStatus(v);
              setPageNumber(0);
            }}
            deviceStatus={deviceStatus}
            onDeviceStatusChange={(v) => {
              setDeviceStatus(v);
              setPageNumber(0);
            }}
            zones={zones}
          />
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="pt-6">
          {error ? (
            <div className="text-center py-8 text-destructive">
              Error loading items: {error.message}
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <>
              <SerializedItemsTable
                items={items}
                onTransfer={(item) => setTransferItem(item)}
                onChangeStatus={(item) => setStatusChangeItem(item)}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {pageNumber * pageLength + 1} to{' '}
                    {Math.min((pageNumber + 1) * pageLength, total)} of {total} items
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber((p) => Math.max(0, p - 1))}
                      disabled={pageNumber === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={pageNumber >= totalPages - 1}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <SerializedItemFormModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreate}
        zones={zones}
        locations={locations}
        isLoading={createMutation.isPending}
      />

      <SerializedItemTransferModal
        open={!!transferItem}
        onClose={() => setTransferItem(null)}
        onTransfer={handleTransfer}
        item={transferItem}
        zones={zones}
        locations={locations}
        isLoading={transferMutation.isPending}
      />

      <DeviceStatusChangeModal
        open={!!statusChangeItem}
        onClose={() => setStatusChangeItem(null)}
        onChangeStatus={handleChangeStatus}
        item={statusChangeItem}
        isLoading={changeStatusMutation.isPending}
      />
    </div>
  );
}
